/**
 * Product image lightbox
 *
 * Expands PDP product images into a fullscreen PhotoSwipe overlay with pinch,
 * wheel and double-click zoom. Enabled per section through the
 * `product_image_feature` setting when it is set to `lightbox`.
 *
 * PhotoSwipe (~21KB gz across core, lightbox and CSS) is fetched on the first
 * click rather than on page load, so a visitor who never opens an image pays
 * nothing beyond this file.
 *
 * ---------------------------------------------------------------------------
 * Do not rewrite the loader below to use dynamic `import()`.
 *
 * This store's asset pipeline runs theme JavaScript through esbuild with a
 * CommonJS output target, which rewrites `import(url)` into
 * `__toESM(require(url))`. `require` does not exist in the browser, so every
 * call throws `ReferenceError: require is not defined`. Files already named
 * `*.min.js` are passed through untouched, which is why the vendored PhotoSwipe
 * builds survive but this file would not.
 *
 * Injecting <script> tags and reading the UMD globals is immune to that
 * transform, so this file is safe to minify.
 * ---------------------------------------------------------------------------
 *
 * Loaded with a plain `defer` rather than the theme's `data-src` lazy-load
 * pattern on purpose: `data-src` scripts are only swapped in on the first user
 * activity event, so a visitor whose very first interaction is a click on a
 * product image would have that click land before the listener existed.
 */
(function () {
  'use strict';

  var GALLERY_SELECTOR = '[data-lightbox-gallery]';

  /** Shared across every gallery on the page — PhotoSwipe only loads once. */
  var photoSwipePromise = null;
  var scriptPromises = {};
  var stylesRequested = false;

  /**
   * Shopify's `asset_url` emits protocol-relative URLs.
   */
  function absoluteUrl(url) {
    if (typeof url !== 'string') return '';
    return url.indexOf('//') === 0 ? window.location.protocol + url : url;
  }

  function readJSON(root, selector) {
    var el = root.querySelector(selector);
    if (!el) return null;

    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      console.error('[product-lightbox] Could not parse ' + selector, error);
      return null;
    }
  }

  function injectStyles(href) {
    if (stylesRequested || !href) return;
    stylesRequested = true;

    var link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = absoluteUrl(href);
    document.head.appendChild(link);
  }

  function loadScript(url) {
    if (scriptPromises[url]) return scriptPromises[url];

    scriptPromises[url] = new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src = url;
      script.async = true;
      script.onload = function () {
        resolve();
      };
      script.onerror = function () {
        delete scriptPromises[url];
        reject(new Error('[product-lightbox] Could not load ' + url));
      };
      document.head.appendChild(script);
    });

    return scriptPromises[url];
  }

  /**
   * Both UMD bundles self-register on `window`. Neither references the other at
   * load time, so they can download in parallel.
   */
  function loadPhotoSwipe(config) {
    if (!photoSwipePromise) {
      photoSwipePromise = Promise.all([
        loadScript(absoluteUrl(config.coreUrl)),
        loadScript(absoluteUrl(config.lightboxUrl))
      ]).then(function () {
        if (!window.PhotoSwipe || !window.PhotoSwipeLightbox) {
          throw new Error('[product-lightbox] PhotoSwipe globals missing after load');
        }

        return { core: window.PhotoSwipe, Lightbox: window.PhotoSwipeLightbox };
      });
    }

    return photoSwipePromise;
  }

  function ProductLightbox(root) {
    this.root = root;
    this.config = readJSON(root, '[data-lightbox-config]') || {};
    this.items = readJSON(root, '[data-lightbox-media]') || [];
    this.lightboxPromise = null;

    if (!this.items.length || !this.config.coreUrl) return;

    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    root.addEventListener('click', this.onClick);
    root.addEventListener('keydown', this.onKeydown);
    root.__productLightbox = this;
  }

  ProductLightbox.prototype.destroy = function () {
    this.root.removeEventListener('click', this.onClick);
    this.root.removeEventListener('keydown', this.onKeydown);
    delete this.root.__productLightbox;

    if (this.lightboxPromise) {
      this.lightboxPromise.then(function (lightbox) {
        lightbox.destroy();
      });
      this.lightboxPromise = null;
    }
  };

  ProductLightbox.prototype.onClick = function (event) {
    var trigger = event.target.closest('[data-lightbox-trigger]');
    if (!trigger) return;

    event.preventDefault();
    this.openFrom(trigger);
  };

  ProductLightbox.prototype.onKeydown = function (event) {
    if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;

    var trigger = event.target.closest('[data-lightbox-trigger]');
    if (!trigger) return;

    // Stop the space key from scrolling the page behind the overlay.
    event.preventDefault();
    this.openFrom(trigger);
  };

  ProductLightbox.prototype.indexOfMedia = function (mediaId) {
    for (var i = 0; i < this.items.length; i++) {
      if (String(this.items[i].id) === String(mediaId)) return i;
    }

    return -1;
  };

  /**
   * The already-rendered thumbnail is in cache, so handing it to PhotoSwipe as
   * `msrc` paints something immediately instead of an empty frame while the
   * full-size image downloads.
   */
  ProductLightbox.prototype.itemsWithPlaceholders = function () {
    var root = this.root;

    return this.items.map(function (item) {
      var copy = Object.assign({}, item);
      var thumb = root.querySelector('[data-lightbox-id="' + item.id + '"]');
      var img = thumb ? (thumb.tagName === 'IMG' ? thumb : thumb.querySelector('img')) : null;

      if (img && img.currentSrc) copy.msrc = img.currentSrc;

      return copy;
    });
  };

  ProductLightbox.prototype.ensureLightbox = function () {
    if (this.lightboxPromise) return this.lightboxPromise;

    var config = this.config;
    injectStyles(config.cssUrl);

    this.lightboxPromise = loadPhotoSwipe(config).then(function (photoSwipe) {
      var lightbox = new photoSwipe.Lightbox({
        // The class itself, not a loader function — PhotoSwipe accepts either,
        // and this keeps dynamic import out of the picture entirely.
        pswpModule: photoSwipe.core,
        bgOpacity: 0.94,
        padding: { top: 24, bottom: 24, left: 16, right: 16 },
        initialZoomLevel: 'fit',
        secondaryZoomLevel: 2,
        maxZoomLevel: 4,
        wheelToZoom: true,
        pinchToClose: true,
        closeTitle: config.closeTitle,
        zoomTitle: config.zoomTitle,
        arrowPrevTitle: config.prevTitle,
        arrowNextTitle: config.nextTitle
      });

      lightbox.init();
      return lightbox;
    });

    return this.lightboxPromise;
  };

  ProductLightbox.prototype.openFrom = function (trigger) {
    var index = this.indexOfMedia(trigger.getAttribute('data-lightbox-id'));
    if (index < 0) return;

    var self = this;

    this.ensureLightbox()
      .then(function (lightbox) {
        lightbox.loadAndOpen(index, self.itemsWithPlaceholders());
      })
      .catch(function (error) {
        console.error('[product-lightbox] Failed to open', error);
        // Let the load be retried on the next click rather than leaving the
        // gallery permanently dead.
        photoSwipePromise = null;
        self.lightboxPromise = null;
      });
  };

  function init(scope) {
    var root = scope || document;
    var galleries = root.querySelectorAll(GALLERY_SELECTOR);

    for (var i = 0; i < galleries.length; i++) {
      if (!galleries[i].__productLightbox) new ProductLightbox(galleries[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var galleries = event.target.querySelectorAll(GALLERY_SELECTOR);

    for (var i = 0; i < galleries.length; i++) {
      if (galleries[i].__productLightbox) galleries[i].__productLightbox.destroy();
    }
  });
})();
